import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Display, H2, H4, Lead } from "@/components/ui/typography"
import { useCountriesData } from "@/hooks/usePricing"
import { useTranslation } from "react-i18next"
import { Star, ArrowRight, Play, Pause } from "lucide-react"
import { CTASection } from "@/components/CTASection"
import { motion } from "motion/react"
import {
  fadeInUp,
  fadeInLeft,
  fadeInRight,
  staggerContainer,
  viewportOnce,
} from "@/lib/animations"
import { AppWindowIcon, WalletIcon, ShieldCheckIcon, HeadsetIcon, LightningIcon, ChartLineIcon, CreditCardIcon, NumpadIcon, PhoneCallIcon } from "@phosphor-icons/react"
import { useState, useRef, useEffect } from "react"
import { trackEvent } from "@/lib/analytics"
import { Link, createFileRoute } from "@tanstack/react-router"
import { HeroDescription } from "@/components/HeroDescription"
import { Marquee } from "@/components/ui/marquee"
import { PricingCountryCard } from "@/components/PricingCountryCard"
import { HeroStats } from "@/components/StatsGrid"
import WorldMap from "@/components/ui/world-map"
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion"
import Arrow30 from "@/assets/30.svg?react"
import Arrow57 from "@/assets/57.svg?react"
import Deco13 from "@/assets/13.svg?react"
import Deco14 from "@/assets/14.svg?react"
import { useLang } from "@/hooks/useLang"
import type { ImageData } from "@responsive-image/core"
import videoPosterData from "@/assets/video-poster.jpg?responsive"
import video480webm from "@/assets/videos/video-480.webm?url"
import video720webm from "@/assets/videos/video-720.webm?url"
import video720mp4 from "@/assets/videos/video-720.mp4?url"
import video1080webm from "@/assets/videos/video-1080.webm?url"

// Pick video sources and a matching poster URL in one go, based on viewport
// width. Poster width is chosen to match the video tier so we don't fetch a
// 1920w image on a phone showing 480p video.
// The media attribute on <source> is not implemented by browsers, so we do
// this in JS instead at click time so it reflects the actual viewport.
function getMediaForWidth(viewportWidth: number, posterData: ImageData): {
  webm: string
  mp4?: string
  poster: string
} {
  // Available widths emitted by the responsive-image plugin: [640, 1024, 1280, 1920]
  // We pick the closest one that's >= the video resolution width.
  const pickPoster = (targetW: number) =>
    posterData.imageUrlFor(targetW, 'webp') ?? posterData.imageUrlFor(targetW) ?? ''

  if (viewportWidth <= 600) {
    return { webm: video480webm, poster: pickPoster(640) }
  }
  if (viewportWidth <= 1200) {
    return { webm: video720webm, mp4: video720mp4, poster: pickPoster(1024) }
  }
  return { webm: video1080webm, poster: pickPoster(1920) }
}

// SSR-safe initial poster: always use the smallest width (640w) so the
// server-rendered HTML never causes a large fetch on any device. The useEffect
// below upgrades it to the viewport-appropriate size after hydration, which
// runs before the user can scroll to and interact with the video.
function getInitialPoster(posterData: ImageData): string {
  const widths = posterData.availableWidths ?? [640]
  const minWidth = Math.min(...widths)
  return posterData.imageUrlFor(minWidth, 'webp') ?? posterData.imageUrlFor(minWidth) ?? ''
}

function VideoPlayer() {
  const videoRef = useRef<HTMLVideoElement>(null)
  const [isPlaying, setIsPlaying] = useState(false)
  // null until first click — avoids fetching video bytes for below-fold content
  // that may never be watched.
  const [media, setMedia] = useState<{ webm: string; mp4?: string; poster: string } | null>(null)

  // SSR renders the 1920w poster (largest, safe default). After hydration swap
  // it to the viewport-appropriate size so phones don't keep a desktop-sized URL.
  // Stored separately so we can update it independently of the click-to-play flow.
  const initialPoster = getInitialPoster(videoPosterData as ImageData)
  const [poster, setPoster] = useState(initialPoster)

  useEffect(() => {
    const { poster: viewportPoster } = getMediaForWidth(window.innerWidth, videoPosterData as ImageData)
    setPoster(viewportPoster)
  }, [])

  const handlePlay = () => {
    if (!media) {
      // First click: resolve viewport-appropriate sources + matching poster
      const chosen = getMediaForWidth(window.innerWidth, videoPosterData as ImageData)
      setMedia(chosen)
      // Playback starts via onLoadedMetadata once sources are in the DOM.
      return
    }
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause()
      } else {
        videoRef.current.play()
      }
      setIsPlaying(!isPlaying)
    }
  }

  return (
    <div
      className="relative rounded-2xl overflow-hidden shadow-2xl bg-black cursor-pointer group"
      onClick={handlePlay}
    >
      <video
        ref={videoRef}
        preload="none"
        playsInline
        width="100%"
        // Use viewport-matched poster after first click, initial poster before
        poster={media?.poster ?? poster}
        className="w-full aspect-video object-cover"
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        onEnded={() => setIsPlaying(false)}
        // Once sources are injected and metadata is ready, start playback
        onLoadedMetadata={() => { videoRef.current?.play() }}
      >
        {media && (
          <>
            <source src={media.webm} type="video/webm" />
            {media.mp4 && <source src={media.mp4} type="video/mp4" />}
          </>
        )}
        Your browser does not support the video tag.
      </video>

      {/* Play/Pause Overlay Button */}
      <div
        className={`absolute inset-0 flex items-center justify-center bg-black/30 transition-opacity duration-300 ${
          isPlaying ? 'opacity-0 group-hover:opacity-100' : 'opacity-100'
        }`}
      >
        <div
          className={`w-24 h-24 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center shadow-xl transition-transform duration-300 hover:scale-110 ${
            isPlaying ? 'scale-90' : 'scale-100'
          }`}
        >
          {isPlaying ? (
            <Pause className="w-10 h-10 text-primary fill-primary" />
          ) : (
            <Play className="w-10 h-10 text-primary fill-primary ml-1" />
          )}
        </div>
      </div>
    </div>
  )
}

export const Route = createFileRoute('/$lang/')({
  staticData: {
    title: 'PhoneSalad — Cheap International Calls',
    description: 'Make affordable international calls to 200+ countries with crystal-clear quality. No contracts, no hidden fees — just low per-minute rates.',
    sitemap: { priority: '1.0', changefreq: 'weekly' },
  },
  component: HomePage,
})

function HomePage() {
    const { popularCountries, stats } = useCountriesData();
    const { t } = useTranslation('marketing');
    const lang = useLang()

    return (
        <div className="min-h-screen bg-background">
            {/* Hero Section */}
            <main>
                <section className="relative pt-20 pb-20 px-4 overflow-hidden">
                    <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_-20%,var(--color-primary)_0%,transparent_70%)] opacity-10 pointer-events-none" />
                    <div className="absolute inset-0 bg-[radial-gradient(circle_at_100%_100%,var(--color-primary)_0%,transparent_50%)] opacity-5 pointer-events-none" />
                    {/* World Map background (desktop only) */}
                    <div className="hidden lg:block absolute right-0 top-[10%] w-[60vw] pointer-events-none z-0">
                        <WorldMap fade />
                    </div>

                    <div className="container mx-auto max-w-6xl relative z-10">
                        <div className="lg:max-w-[75%]">
                            <div className="text-center lg:text-left">
                                <div
                                    className="relative inline-block animate-fade-in-up opacity-0"
                                    style={{ animationDelay: '100ms' }}
                                >
                                    <Deco13 className="absolute w-12 h-12 text-primary/30 -left-16 top-0 hidden lg:block" />
                                    <Display className="mb-6 leading-tight">
                                        {t('hero.title')}
                                        <br />
                                        <span className="relative text-primary">
                                            {t('hero.titleHighlight')}
                                            <Deco14 className="absolute w-10 h-10 text-primary/30 -right-12 -top-4 hidden lg:block" />
                                        </span>
                                    </Display>
                                </div>

                                <div
                                    className="animate-fade-in-up opacity-0"
                                    style={{ animationDelay: '220ms' }}
                                >
                                    <HeroDescription popularCountries={popularCountries || []} />
                                </div>

                                <div
                                    className="flex justify-center lg:justify-start mb-12 lg:mb-0 animate-fade-in-up opacity-0"
                                    style={{ animationDelay: '340ms' }}
                                >
                                    <Button size="lg" className="text-lg px-8 py-6 group" asChild onClickCapture={() => trackEvent('cta-click', { label: 'hero-start-calling' })}>
                                        <Link to="/dialer" className="flex items-center gap-2">
                                            {t('hero.startCalling')}
                                            <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" strokeWidth={2.5} />
                                        </Link>
                                    </Button>
                                </div>
                            </div>
                        </div>

                        {/* Stars + Stats (full width below) */}
                        <div
                            className="mt-16 animate-fade-in-up opacity-0"
                            style={{ animationDelay: '460ms' }}
                        >
                            <div className="flex items-center justify-center gap-1 mb-6">
                                {[...Array(5)].map((_, i) => (
                                    <div
                                        key={i}
                                        className="animate-scale-in opacity-0"
                                        style={{ animationDelay: `${580 + i * 100}ms` }}
                                    >
                                        <Star className="w-5 h-5 fill-[#FDB72D] text-[#FDB72D]" />
                                    </div>
                                ))}
                            </div>

                            <HeroStats
                                totalCountries={stats?.totalCountries}
                                minRate={stats?.minRate}
                            />
                        </div>
                    </div>
                </section>

                {/* Features Section */}
                <section id="features" className="py-20 px-4 bg-muted/30">
                    <div className="container mx-auto max-w-6xl">
                        <motion.div
                            className="text-center mb-16"
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={viewportOnce}
                            transition={{ duration: 0.5 }}
                        >
                            <H2 className="mb-4">{t('features.title')}</H2>
                            <Lead>
                                {t('features.description')}
                            </Lead>
                        </motion.div>

                        <motion.div
                            className="grid md:grid-cols-3 gap-6"
                            initial="hidden"
                            whileInView="visible"
                            viewport={viewportOnce}
                            variants={staggerContainer}
                        >
                            {[
                                { icon: AppWindowIcon, key: 'noApps' },
                                { icon: WalletIcon, key: 'payAsYouGo' },
                                { icon: ShieldCheckIcon, key: 'secure' },
                                { icon: HeadsetIcon, key: 'hdVoice' },
                                { icon: LightningIcon, key: 'instant' },
                                { icon: ChartLineIcon, key: 'analytics' },
                            ].map(({ icon: Icon, key }) => (
                                <motion.div key={key} variants={fadeInUp}>
                                    <Card variant="ghost">
                                        <CardContent>
                                            <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                                                <Icon weight="duotone" className="w-8 h-8" style={{ color: 'var(--color-primary)' }} />
                                            </div>
                                            <H4 className="mb-2">{t(`features.${key}.title`)}</H4>
                                            <p className="text-muted-foreground">{t(`features.${key}.description`)}</p>
                                        </CardContent>
                                    </Card>
                                </motion.div>
                            ))}
                        </motion.div>
                    </div>
                </section>

                {/* Popular Destinations */}
                <section className="py-32 px-4 relative overflow-hidden min-h-[600px]">
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-0">
                        <div className="w-full max-w-[1400px]">
                            <WorldMap showConnections={false} fade />
                        </div>
                    </div>
                    <div className="absolute inset-0 bg-background/50 z-[2]" />

                    <div className="container mx-auto max-w-6xl relative z-10">
                        <motion.div
                            className="text-center mb-12"
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={viewportOnce}
                            transition={{ duration: 0.5 }}
                        >
                            <H2 className="mb-4">{t('pricing.homeSectionTitle')}</H2>
                            <Lead>
                                {t('pricing.homeSectionDescription', { count: Math.floor((stats?.totalCountries ?? 0) / 10) * 10 })}
                            </Lead>
                        </motion.div>

                        {popularCountries && popularCountries.length > 0 && (
                            <motion.div
                                className="space-y-4"
                                initial={{ opacity: 0 }}
                                whileInView={{ opacity: 1 }}
                                viewport={viewportOnce}
                                transition={{ duration: 0.6, delay: 0.2 }}
                            >
                                <Marquee pauseOnHover className="[--duration:30s] [--gap:1rem]">
                                    {popularCountries.map((country) => (
                                        <PricingCountryCard
                                            key={country.countryCode}
                                            country={country}
                                            className="min-w-[200px] flex-shrink-0"
                                        />
                                    ))}
                                </Marquee>
                                <Marquee pauseOnHover reverse className="[--duration:35s] [--gap:1rem]">
                                    {popularCountries.map((country) => (
                                        <PricingCountryCard
                                            key={country.countryCode}
                                            country={country}
                                            className="min-w-[200px] flex-shrink-0"
                                        />
                                    ))}
                                </Marquee>
                            </motion.div>
                        )}

                        <motion.div
                            className="text-center mt-8"
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={viewportOnce}
                            transition={{ delay: 0.4, duration: 0.5 }}
                        >
                            <Button variant="outline" size="lg" asChild>
                                <Link to="/$lang/pricing" params={{ lang }}>
                                    {t('pricing.allCountries', { count: Math.floor((stats?.totalCountries ?? 0) / 10) * 10 })}
                                </Link>
                            </Button>
                        </motion.div>
                    </div>
                </section>

                {/* How It Works */}
                <section className="py-20 px-4 bg-muted/30">
                    <div className="container mx-auto max-w-4xl text-center">
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={viewportOnce}
                            transition={{ duration: 0.5 }}
                        >
                            <H2 className="mb-16">{t('howItWorks.title')}</H2>
                        </motion.div>

                        <div className="relative grid md:grid-cols-3 gap-12">
                            {[
                                { icon: CreditCardIcon, key: 'addCredits' },
                                { icon: NumpadIcon, key: 'dialCall' },
                                { icon: PhoneCallIcon, key: 'payPerUse' },
                            ].map(({ icon: Icon, key }, index) => (
                                <motion.div
                                    key={key}
                                    initial={{ opacity: 0, y: 30 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true, margin: "-50px" }}
                                    transition={{ duration: 0.5, delay: index * 0.15 }}
                                >
                                    <div className="bg-primary/5 rounded-2xl px-8 py-10 flex flex-col items-center justify-center gap-4 mb-6 mx-auto">
                                        <Icon weight="duotone" className="w-20 h-20" style={{ color: 'var(--color-primary)' }} />
                                        <H4>{t(`howItWorks.steps.${key}.title`)}</H4>
                                    </div>
                                    <p className="text-muted-foreground">{t(`howItWorks.steps.${key}.description`)}</p>
                                </motion.div>
                            ))}

                            <Arrow57 className="hidden md:block absolute w-16 h-32 text-primary/30 left-[30%] top-16 -translate-x-1/2 rotate-270" />
                            <Arrow30 className="hidden md:block absolute w-8 h-32 text-primary/30 left-[66%] top-16 -translate-x-1/2 rotate-270" />
                        </div>
                    </div>
                </section>

                {/* Demo Video Section */}
                <section className="py-20 px-4">
                    <div className="container mx-auto max-w-5xl">
                        <motion.div
                            className="text-center mb-12"
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={viewportOnce}
                            transition={{ duration: 0.5 }}
                        >
                            <H2 className="mb-4">{t('demoVideo.title', 'See It In Action')}</H2>
                            <Lead>
                                {t('demoVideo.description', 'Watch how easy it is to make international calls with PhoneSalad')}
                            </Lead>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, scale: 0.95 }}
                            whileInView={{ opacity: 1, scale: 1 }}
                            viewport={viewportOnce}
                            transition={{ duration: 0.6, delay: 0.2 }}
                        >
                            <VideoPlayer />
                        </motion.div>
                    </div>
                </section>

                {/* FAQ Section */}
                <section className="py-20 px-4">
                    <div className="container mx-auto max-w-3xl">
                        <motion.div
                            className="text-center mb-12"
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={viewportOnce}
                            transition={{ duration: 0.5 }}
                        >
                            <H2 className="mb-4">{t('faq.title')}</H2>
                            <Lead>
                                {t('faq.description')}
                            </Lead>
                        </motion.div>

                        <Accordion type="single" collapsible className="space-y-4 pb-1">
                            {(Array.isArray(t('faq.items', { returnObjects: true })) ? t('faq.items', { returnObjects: true }) as any[] : []).filter(item => item.featured).map((item, index) => (
                                <motion.div
                                    key={item.id}
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ delay: index * 0.1, duration: 0.4 }}
                                >
                                    <AccordionItem
                                        value={item.id}
                                        className="group border rounded-xl bg-card px-6"
                                    >
                                        <AccordionTrigger className="text-lg hover:no-underline">
                                            {item.question}
                                        </AccordionTrigger>
                                        <AccordionContent className="text-muted-foreground leading-relaxed">
                                            {item.answer}
                                        </AccordionContent>
                                    </AccordionItem>
                                </motion.div>
                            ))}
                        </Accordion>

                        <motion.div
                            className="text-center mt-12"
                            initial={{ opacity: 0 }}
                            whileInView={{ opacity: 1 }}
                            viewport={{ once: true }}
                            transition={{ delay: 0.3, duration: 0.5 }}
                        >
                            <Button variant="outline" size="lg" className="group" asChild>
                                <Link to="/$lang/faq" params={{ lang }}>
                                    {t('faq.viewAll')}
                                    <ArrowRight className="w-5 h-5 ml-2 transition-transform group-hover:translate-x-1" strokeWidth={2.5} />
                                </Link>
                            </Button>
                        </motion.div>
                    </div>
                </section>

                {/* CTA Section */}
                <CTASection
                    variant="primary"
                    translationKeyRoot="cta"
                    buttonTo="/dialer"
                    buttonKey="getStarted"
                    noteKey="noCommitment"
                    onButtonClick={() => trackEvent('cta-click', { label: 'bottom-get-started' })}
                />
            </main>
        </div>
    )
}
